import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { loadAllArticles } from '@/services/articleLoader';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';
import { Tv, Cpu, ChevronRight } from 'lucide-react';
import { getArticleImages, getOptimizedSearchQuery } from '@/services/imageService';

export const DeviceGuidesPage = () => {
    const [loadedArticles, setLoadedArticles] = useState([]);
    const [featuredImages, setFeaturedImages] = useState({});

    useEffect(() => {
        const loadArticles = async () => {
            try {
                const articles = await loadAllArticles();
                if (articles && articles.length > 0) {
                    // Filter for Device Guides
                    const deviceArticles = articles.filter(a =>
                        a.category === 'Devices' ||
                        a.category === 'Setup Guides' ||
                        a.tags?.some(tag => ['FireStick', 'Android TV', 'Smart TV', 'iOS', 'Windows', 'Setup Guides', 'setup-guides'].includes(tag))
                    );
                    setLoadedArticles(deviceArticles);
                }
            } catch (error) {
                console.error('Error loading device articles', error);
            }
        };
        loadArticles();
    }, []);

    useEffect(() => {
        const fetchImages = async () => {
            if (loadedArticles.length === 0) return;
            const images = {};
            for (const article of loadedArticles) {
                try {
                    const searchQuery = getOptimizedSearchQuery(article.title);
                    const result = await getArticleImages(searchQuery, 1);
                    images[article.id] = result.length > 0 ? result[0] : { placeholder: true, color: 'from-purple-600 to-indigo-900' };
                } catch (e) {
                    images[article.id] = { placeholder: true, color: 'from-purple-600 to-indigo-900' };
                }
            }
            setFeaturedImages(images);
        };
        fetchImages();
    }, [loadedArticles]);

    const getFeaturedImage = (article) => featuredImages[article.id] || { placeholder: true, color: 'from-purple-600 to-indigo-900' };

    return (
        <>
            <Helmet>
                <title>Device Setup Guides & Tutorials | XtreamGate</title>
                <meta name="description" content="Master your streaming device with our easy-to-follow setup guides. FireStick, Android TV, Apple TV, Smart TVs, and more." />
            </Helmet>

            <MainNavigation />

            <div className="min-h-screen bg-gradient-to-b from-background via-background to-purple-950/20">
                <section className="pt-32 pb-16 px-4 text-center overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl">
                        <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px]" />
                    </div>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
                            <Tv className="w-4 h-4 text-purple-400" />
                            <span className="text-sm font-bold text-purple-400 uppercase tracking-wider">Device Lab</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            Setup & <span className="text-purple-500">Guides</span>
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Unlock the full potential of your hardware. From FireSticks to Smart TVs, we provide step-by-step instructions to get you streaming in minutes.
                        </p>
                    </motion.div>
                </section>

                <div className="max-w-7xl mx-auto px-4 pb-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {loadedArticles.map((article, index) => {
                            const imageData = getFeaturedImage(article);
                            return (
                                <motion.div
                                    key={article.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <Link to={article.canonicalPath || `/blog/${article.slug}`} className="group block h-full bg-card/40 border border-white/5 rounded-2xl overflow-hidden hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-300">
                                        <div className={`h-48 relative overflow-hidden ${imageData.placeholder ? `bg-gradient-to-br ${imageData.color}` : 'bg-gray-900'}`}>
                                            {imageData.url && <img src={imageData.url} alt={article.title} className="w-full h-full object-cover group-hover:scale-110 transition duration-700" />}
                                            <div className="absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-black/80 to-transparent">
                                                <div className="flex items-center gap-2 text-white/90 font-medium">
                                                    <Cpu className="w-4 h-4 text-purple-400" />
                                                    {article.category}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-6">
                                            <h3 className="text-xl font-bold mb-3 group-hover:text-purple-400 transition">{article.title}</h3>
                                            <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{article.excerpt}</p>
                                            <div className="flex items-center text-sm font-semibold text-purple-500">
                                                View Solution <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
};
